import { memo, useMemo } from 'react';
import { Pressable, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Caption, Body } from '@/components/ui/Text';
import type { ItineraryItem } from '@/services/itinerary';
import type { TripDestination } from '@/services/trips';
import { coerceDate, toLocalDateKey } from '@/utils/dateKeys';

export type ItineraryItemCompactCardProps = {
  item: ItineraryItem;
  tripDestinations?: TripDestination[];
  contextDateKey?: string;
  onPress?: () => void;
  conflictSeverity?: 'error' | 'warning' | null;
  conflictMessage?: string | null;
  conflictActionLabel?: string;
  onConflictActionPress?: () => void;
  showTimeRange?: boolean;
  showSecondaryLine?: boolean;
};

const getCategoryLabel = (category: ItineraryItem['category']): string => {
  switch (category) {
    case 'flight':
      return 'Flight';
    case 'hotel':
      return 'Hotel';
    case 'activity':
      return 'Activity';
    case 'ferry':
      return 'Ferry';
    case 'train':
      return 'Train';
    case 'transportation':
      return 'Transit';
    default:
      return 'Event';
  }
};

const getCategoryIcon = (category: ItineraryItem['category']): keyof typeof Ionicons.glyphMap => {
  switch (category) {
    case 'flight':
      return 'airplane-outline';
    case 'hotel':
      return 'bed-outline';
    case 'activity':
      return 'ticket-outline';
    case 'ferry':
      return 'boat-outline';
    case 'train':
      return 'train-outline';
    case 'transportation':
      return 'bus-outline';
    default:
      return 'calendar-outline';
  }
};

const formatTime = (time?: string): string | null => {
  if (!time || time === 'flexible') return null;
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  if (Number.isNaN(hour)) return null;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes || '00'} ${ampm}`;
};

const formatDuration = (minutes: number): string => {
  if (!Number.isFinite(minutes) || minutes <= 0) return '';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

function ItineraryItemCompactCardComponent({
  item,
  tripDestinations,
  contextDateKey,
  onPress,
  conflictSeverity = null,
  conflictMessage = null,
  conflictActionLabel,
  onConflictActionPress,
  showTimeRange = true,
  showSecondaryLine = true,
}: ItineraryItemCompactCardProps) {
  const theme = useTheme();

  const leftAccentColor =
    conflictSeverity === 'error'
      ? theme.colors.error
      : conflictSeverity === 'warning'
        ? theme.colors.warning
        : null;

  const categoryIcon = getCategoryIcon(item.category);
  const categoryLabel = getCategoryLabel(item.category);

  const assignedCityLabel = useMemo(() => {
    // Only show the "City" badge for true multi-city trips.
    // Some template/legacy items may have cityIndex set even when the trip only has one destination,
    // which can cause the badge to display an incorrect label (e.g., template name).
    if (!tripDestinations || tripDestinations.length <= 1) return null;
    if (typeof item.cityIndex !== 'number') return null;
    const city = tripDestinations?.[item.cityIndex]?.city;
    return city ? city : null;
  }, [item.cityIndex, tripDestinations]);

  const timeLabel = useMemo(() => {
    if (!showTimeRange) return null;
    if (!item.startTime || item.startTime === 'flexible') return 'Flexible';
    const start = formatTime(item.startTime);
    const end = item.endTime ? formatTime(item.endTime) : null;
    if (!start) return null;
    return end ? `${start}–${end}` : start;
  }, [item.startTime, item.endTime, showTimeRange]);

  const durationLabel = useMemo(() => {
    if (!item.startTime || item.startTime === 'flexible' || !item.endTime) return null;
    const [sh, sm] = item.startTime.split(':').map((v) => parseInt(v, 10));
    const [eh, em] = item.endTime.split(':').map((v) => parseInt(v, 10));
    if (![sh, sm, eh, em].every((n) => Number.isFinite(n))) return null;
    const diff = eh * 60 + em - (sh * 60 + sm);
    if (diff <= 0) return null;
    return formatDuration(diff);
  }, [item.startTime, item.endTime]);

  const secondaryText = useMemo(() => {
    if (!showSecondaryLine) return null;
    const loc = (item.location || item.address || '').trim();
    if (loc) return loc;
    const desc = (item.description || '').trim();
    if (!desc) return null;
    return desc.length > 80 ? `${desc.slice(0, 80).trim()}…` : desc;
  }, [item.location, item.address, item.description, showSecondaryLine]);

  const rangeBadge = useMemo(() => {
    if (!contextDateKey) return null;
    const startDate = coerceDate(item.startDate);
    const endDate = item.endDate ? coerceDate(item.endDate) : null;
    if (!startDate || !endDate) return null;
    const startKey = toLocalDateKey(startDate);
    const endKey = toLocalDateKey(endDate);
    if (startKey === endKey) return null;
    if (contextDateKey === startKey) return 'Starts today';
    if (contextDateKey === endKey) return 'Ends today';
    return null;
  }, [contextDateKey, item.startDate, item.endDate]);

  const conflictContext = useMemo(() => {
    const msg = (conflictMessage || '').trim();
    if (!conflictSeverity || !msg) return null;
    // Let the UI handle line clamping; avoid truncating the message string itself.
    return msg;
  }, [conflictMessage, conflictSeverity]);

  return (
    <Pressable
      onPress={onPress}
      disabled={!onPress}
      style={({ pressed }) => ({
        opacity: pressed ? 0.85 : 1,
      })}
      accessibilityRole={onPress ? 'button' : undefined}
      accessibilityLabel={`Open details for ${item.title}`}
    >
      <Card
        variant="elevated"
        elevation={1}
        style={{
          padding: theme.spacing(1.75),
          borderLeftWidth: leftAccentColor ? 3 : 0,
          borderLeftColor: leftAccentColor || 'transparent',
          gap: theme.spacing(0.75),
        }}
      >
        {!!onPress && (
          <Ionicons
            name="chevron-forward"
            size={18}
            color={theme.colors.textSecondary}
            style={{
              position: 'absolute',
              right: theme.spacing(1.25),
              top: theme.spacing(1.75),
            }}
          />
        )}

        <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: theme.spacing(1.25) }}>
          <View
            style={{
              width: 40,
              height: 40,
              borderRadius: theme.radii.lg,
              backgroundColor: theme.colors.borderMuted + '20',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            <Ionicons name={categoryIcon} size={18} color={theme.colors.textSecondary} accessible={false} />
          </View>

          <View style={{ flex: 1, gap: theme.spacing(0.5), paddingRight: onPress ? theme.spacing(2.5) : 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.75), flexWrap: 'wrap' }}>
              <ThemedText variant="bodyStrong" style={{ fontSize: 16, fontWeight: '700', flexShrink: 1 }}>
                {item.title}
              </ThemedText>
              <View
                style={{
                  paddingHorizontal: theme.spacing(1),
                  paddingVertical: 3,
                  borderRadius: theme.radii.pill,
                  backgroundColor: theme.colors.primary + '12',
                }}
                accessibilityRole="text"
              >
                <Caption style={{ fontSize: 11, fontWeight: '600', color: theme.colors.primary }}>
                  {categoryLabel}
                </Caption>
              </View>
              {!!assignedCityLabel && (
                <View
                  style={{
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: 3,
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.textSecondary + '10',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4,
                  }}
                  accessibilityRole="text"
                  accessibilityLabel={`City: ${assignedCityLabel}`}
                >
                  <Ionicons name="location-outline" size={12} color={theme.colors.textSecondary} accessible={false} />
                  <Caption style={{ fontSize: 11, fontWeight: '700', color: theme.colors.textSecondary }}>
                    {assignedCityLabel}
                  </Caption>
                </View>
              )}
              {conflictSeverity && (
                <View
                  style={{
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: 3,
                    borderRadius: theme.radii.pill,
                    backgroundColor: (conflictSeverity === 'error' ? theme.colors.error : theme.colors.warning) + '12',
                  }}
                  accessibilityRole="text"
                  accessibilityLabel="Scheduling conflict"
                >
                  <Caption
                    style={{
                      fontSize: 11,
                      fontWeight: '700',
                      color: conflictSeverity === 'error' ? theme.colors.error : theme.colors.warning,
                    }}
                  >
                    Conflict
                  </Caption>
                </View>
              )}
              {!!rangeBadge && (
                <View
                  style={{
                    paddingHorizontal: theme.spacing(1.25),
                    paddingVertical: 3,
                    borderRadius: theme.radii.pill,
                    backgroundColor: theme.colors.borderMuted + '35',
                    borderWidth: 1,
                    borderColor: theme.colors.borderMuted + '75',
                  }}
                  accessibilityRole="text"
                  accessibilityLabel={rangeBadge}
                >
                  <Caption style={{ fontSize: 11, fontWeight: '700', color: theme.colors.text }}>
                    {rangeBadge}
                  </Caption>
                </View>
              )}
              {!!conflictSeverity && !!conflictActionLabel && !!onConflictActionPress && (
                <Pressable
                  onPress={(e: any) => {
                    e?.stopPropagation?.();
                    onConflictActionPress();
                  }}
                  style={({ pressed }) => ({
                    opacity: pressed ? 0.75 : 1,
                    paddingHorizontal: theme.spacing(1),
                    paddingVertical: 3,
                    borderRadius: theme.radii.pill,
                    backgroundColor:
                      (conflictSeverity === 'error' ? theme.colors.error : theme.colors.warning) + '12',
                    borderWidth: 1,
                    borderColor:
                      (conflictSeverity === 'error' ? theme.colors.error : theme.colors.warning) + '35',
                  })}
                  accessibilityRole="button"
                  accessibilityLabel={conflictActionLabel}
                  accessibilityHint="Opens tools to resolve the scheduling conflict"
                >
                  <Caption
                    style={{
                      fontSize: 11,
                      fontWeight: '800',
                      color: conflictSeverity === 'error' ? theme.colors.error : theme.colors.warning,
                    }}
                  >
                    {conflictActionLabel}
                  </Caption>
                </Pressable>
              )}
            </View>

            {!!conflictContext && (
              <Caption
                color={conflictSeverity === 'error' ? theme.colors.error : (theme.colors.warning || theme.colors.textSecondary)}
                style={{ fontSize: 12, fontWeight: '600' }}
                numberOfLines={3}
              >
                {conflictContext}
              </Caption>
            )}

            {!!timeLabel && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="time-outline" size={14} color={theme.colors.textSecondary} accessible={false} />
                <Caption style={{ color: theme.colors.textSecondary, fontSize: 12, fontWeight: '600' }}>
                  {timeLabel}
                  {durationLabel ? ` • ${durationLabel}` : ''}
                </Caption>
              </View>
            )}

            {!!secondaryText && (
              <Body
                color={theme.colors.textSecondary}
                style={{ fontSize: 13, lineHeight: 18 }}
                numberOfLines={1}
              >
                {secondaryText}
              </Body>
            )}
          </View>
        </View>
      </Card>
    </Pressable>
  );
}

export const ItineraryItemCompactCard = memo(ItineraryItemCompactCardComponent);


